import { useCallback, useEffect, useRef } from "react"
import {
	IconChevronRightOutline18 as ChevronRight,
	IconBoltLightningOutline18 as BoltLightning,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import { defaultAiChatWelcomeMessage } from "@productlane/lib/src/ai-chat-defaults"
import { starter } from "@productlane/lib/src/plans-client"
import {
	Button,
	Popover,
	Switch,
	Textarea,
	TriggerActionCard,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { docsDefaultExampleQuestions } from "@/constants"
import { analytics } from "@/lib/analytics"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { useRequiredPlans } from "@/lib/plans"
import { useAutosaveText } from "@/lib/use-autosave-text"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import {
	RoutineCapabilitiesPopover,
	RoutineLimitsRow,
} from "./routine-capabilities-panel"
import {
	appendLine,
	remainingExamples,
	remainingUseCases,
	ROUTINE_DEFAULT_PROMPT,
} from "./routine-examples"

const FormSchema = z.object({ routinesPrompt: z.string().optional() })

const DIVIDER = "border-t border-gray-200 dark:border-gray-700/80"

export function RoutinesSection() {
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const { passes: hasRequiredPlan } = useRequiredPlans(starter.andUp)

	const settings = workspace?.aiChatSettings
	const enabled = settings?.routinesEnabled ?? false
	const persisted = settings?.routinesPrompt ?? ""

	const form = useZodForm({
		schema: FormSchema,
		defaultValues: { routinesPrompt: persisted },
	})

	const initializedRef = useRef(false)
	useEffect(() => {
		if (!settings) return
		if (initializedRef.current) return
		initializedRef.current = true
		form.reset({ routinesPrompt: settings.routinesPrompt ?? "" })
	}, [settings, form])

	const createdSettingsIdRef = useRef<string | null>(null)
	const watchedPrompt = form.watch("routinesPrompt") ?? ""

	const persist = useCallback(
		async (values: { routinesEnabled?: boolean; routinesPrompt?: string }) => {
			if (!workspace) return
			const settingsId =
				workspace.aiChatSettings?.id ?? createdSettingsIdRef.current
			const routinesPrompt =
				values.routinesPrompt === undefined
					? undefined
					: values.routinesPrompt.trim().length > 0
						? values.routinesPrompt
						: null

			if (!settingsId) {
				const id = generateId()
				createdSettingsIdRef.current = id
				await mutateOk(
					zero.mutate(
						mutators.aiChatSettings.create({
							id,
							workspaceId: workspace.id,
							welcomeMessage: defaultAiChatWelcomeMessage(workspace.name),
							exampleQuestions: docsDefaultExampleQuestions,
							isEnabled: false,
							routinesEnabled: values.routinesEnabled ?? false,
							routinesPrompt,
						}),
					),
				)
				return
			}
			await mutateOk(
				zero.mutate(
					mutators.aiChatSettings.update({
						id: settingsId,
						routinesEnabled: values.routinesEnabled,
						routinesPrompt,
					}),
				),
			)
		},
		[workspace, zero],
	)

	const savePrompt = useCallback(
		async (value: string) => {
			try {
				await persist({ routinesPrompt: value })
			} catch (error) {
				captureError(error, {
					domain: "ai-chat",
					action: "update routines prompt",
					workspaceId: workspace?.id,
				})
				// Keep the text in the field; a rollback would throw away the writing.
				toast.error({
					title: "Your routine didn't save",
					description: "Reload the page, then apply your change again.",
				})
			}
		},
		[persist, workspace?.id],
	)

	const flushPrompt = useAutosaveText({
		value: watchedPrompt,
		persistedValue: persisted,
		onSave: savePrompt,
		enabled: !!form.formState.dirtyFields.routinesPrompt,
	})

	if (!workspace) return null

	const handleToggle = async (next: boolean, seedLine?: string) => {
		try {
			// A routine with nothing to read still tags and sets importance, but a
			// seeded line makes what it can do visible from the first look.
			const seed = seedLine?.trim() || ROUTINE_DEFAULT_PROMPT
			const seedPrompt = next && !persisted.trim() ? seed : undefined
			if (seedPrompt) {
				form.setValue("routinesPrompt", seedPrompt)
			}
			await persist({ routinesEnabled: next, routinesPrompt: seedPrompt })
			toast.success({
				title: next ? "Routines enabled" : "Routines disabled",
				icon: BoltLightning,
			})
			analytics.track("toggle-routines", { enabled: next })
		} catch (error) {
			captureError(error, {
				domain: "ai-chat",
				action: "update routines settings",
				workspaceId: workspace.id,
			})
			toast.error({ title: "Failed to update" })
		}
	}

	const addExample = (line: string) => {
		const next = appendLine(watchedPrompt, line)
		if (next === watchedPrompt) return
		form.setValue("routinesPrompt", next, { shouldDirty: true })
		void savePrompt(next)
	}

	const showInstructions = enabled && hasRequiredPlan
	const examples = remainingExamples(watchedPrompt)
	const useCases = remainingUseCases(watchedPrompt)

	const capabilitiesTrigger = (
		<Popover
			align="end"
			className="w-80"
			content={<RoutineCapabilitiesPopover />}
		>
			<Button variant="link" size="sm" iconRight={ChevronRight}>
				What it can do
			</Button>
		</Popover>
	)

	return (
		<div className="mt-1">
			<SettingsContainer className="divide-none p-4">
				<div className="flex flex-col">
					<SettingItem
						title="Run a routine on every new thread"
						description="It reads each thread as it arrives, then acts."
						action={
							hasRequiredPlan ? (
								<Switch
									name="routinesEnabled"
									checked={enabled}
									onCheckedChange={handleToggle}
								/>
							) : (
								<UpgradeButton required={["starter", "pro", "scale"]} />
							)
						}
					/>

					<div className={`mt-4 pt-4 ${DIVIDER}`}>
						{showInstructions ? (
							<div className="flex flex-col gap-3">
								<div className="flex flex-wrap items-center justify-between gap-2">
									<div>
										<div className="text-[13px] font-medium">Instructions</div>
										<p className="mt-0.5 text-[12px] leading-relaxed text-tertiary">
											Plain language, one instruction per line.
										</p>
									</div>
									{capabilitiesTrigger}
								</div>

								<Textarea
									variant="settings"
									{...form.register("routinesPrompt")}
									placeholder="e.g. Assign threads that mention pricing to Sam."
									minRows={4}
									onBlur={(e) => {
										form.setValue("routinesPrompt", e.target.value)
										flushPrompt(e.target.value)
									}}
								/>

								{examples.length > 0 && (
									<div className="flex flex-col gap-2">
										<div className="text-[12px] text-tertiary">
											Add another instruction
										</div>
										<div className="grid gap-2 sm:grid-cols-2">
											{examples.map((example) => (
												<TriggerActionCard
													key={example.id}
													Icon={example.Icon}
													iconClassName={example.iconClassName}
													title={example.title}
													description={example.description}
													TriggerIcon={example.trigger.Icon}
													triggerLabel={example.trigger.label}
													ActionIcon={example.action.Icon}
													actionLabel={example.action.label}
													showPointer={false}
													onClick={() => addExample(example.line)}
												/>
											))}
										</div>
									</div>
								)}
							</div>
						) : (
							<div className="flex flex-col gap-3">
								<div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
									<p className="text-[12.5px] leading-relaxed text-secondary">
										{hasRequiredPlan
											? "Every new thread arrives sorted, before anyone opens it."
											: "A routine reads each new thread and triages it for you."}
									</p>
									{capabilitiesTrigger}
								</div>
								<div className="grid gap-2 sm:grid-cols-2">
									{useCases.map((useCase) => (
										<TriggerActionCard
											key={useCase.id}
											Icon={useCase.Icon}
											iconClassName={useCase.iconClassName}
											title={useCase.title}
											description={useCase.description}
											TriggerIcon={useCase.trigger.Icon}
											triggerLabel={useCase.trigger.label}
											ActionIcon={useCase.action.Icon}
											actionLabel={useCase.action.label}
											disabled={!hasRequiredPlan}
											showPointer={false}
											onClick={() => void handleToggle(true, useCase.line)}
										/>
									))}
								</div>
							</div>
						)}
					</div>

					<div className={`mt-4 pt-3 ${DIVIDER}`}>
						<RoutineLimitsRow />
					</div>
				</div>
			</SettingsContainer>
		</div>
	)
}
